import { useEffect, useMemo, useState } from "react";
import { ref, onValue, update, remove } from "firebase/database";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Music, Edit2, Trash2, Search, Folder } from "lucide-react";

export default function ArtistManagement({ db }) {
  const [artists, setArtists] = useState([]);
  const [music, setMusic] = useState([]);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 6;
  const [editingArtist, setEditingArtist] = useState(null);
  const [editingTrack, setEditingTrack] = useState(null);
  const [expandedArtist, setExpandedArtist] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: "",
    message: "",
    onConfirm: null,
    onCancel: null
  });

  // Listen: artists
  useEffect(() => {
    if (!db) {
      setLoading(false);
      return;
    }
    
    const artistsRef = ref(db, "artists");
    const unsub = onValue(
      artistsRef,
      (snap) => {
        const data = snap.val() || {};
        const arr = Object.entries(data).map(([id, v]) => ({ id, ...v }));
        console.log("Artists loaded:", arr);
        setArtists(arr);
      },
      (error) => {
        console.error("Error loading artists:", error);
        setArtists([]);
      }
    );
    return () => unsub();
  }, [db]);

  // Listen: music
  useEffect(() => {
    if (!db) {
      setLoading(false);
      return;
    }
    
    const musicRef = ref(db, "artistContent/music");
    const unsub = onValue(
      musicRef,
      (snap) => {
        const data = snap.val() || {};
        const arr = Object.entries(data).map(([id, v]) => ({ id, ...v }));
        console.log("Music loaded:", arr);
        setMusic(arr);
        setLoading(false);
      },
      (error) => {
        console.error("Error loading music:", error);
        setMusic([]);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [db]);

  // Group music by artistId
  const musicGroupedByArtist = useMemo(() => {
    const grouped = {};
    for (const t of music) {
      const aid = t.artistId || t.artist || "unknown";
      if (!grouped[aid]) grouped[aid] = [];
      grouped[aid].push(t);
    }
    return grouped;
  }, [music]);

  // Merge artists with their music
  const merged = useMemo(() => {
    return artists.map((a) => {
      const tracks = musicGroupedByArtist[a.id] || [];
      const byAlbum = {};
      for (const tr of tracks) {
        const albumName = tr.album || "Singles";
        if (!byAlbum[albumName]) byAlbum[albumName] = [];
        byAlbum[albumName].push(tr);
      }
      return { ...a, tracks, albums: byAlbum };
    });
  }, [artists, musicGroupedByArtist]);

  // Search filter
  const filtered = useMemo(() => {
    if (!query || query.trim() === "") return merged;
    const q = query.toLowerCase();
    return merged
      .map((a) => {
        const artistMatch =
          (a.name && a.name.toLowerCase().includes(q)) ||
          (a.email && a.email.toLowerCase().includes(q)) ||
          (a.subscriptionTier && a.subscriptionTier.toLowerCase().includes(q));

        const filteredAlbums = {};
        let anyTrackMatch = false;
        for (const [album, tracks] of Object.entries(a.albums || {})) {
          const matches = tracks.filter(
            (t) =>
              (t.name && t.name.toLowerCase().includes(q)) ||
              (t.artistName && t.artistName.toLowerCase().includes(q)) ||
              (album && album.toLowerCase().includes(q))
          );
          if (matches.length) {
            filteredAlbums[album] = matches;
            anyTrackMatch = true;
          }
        }

        if (artistMatch || anyTrackMatch) {
          return {
            ...a,
            albums: artistMatch ? a.albums : filteredAlbums,
            tracks: artistMatch ? a.tracks : Object.values(filteredAlbums).flat(),
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [merged, query]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const paginated = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  // Save/Delete handlers
  const saveArtist = async (id, updates) => {
    try {
      await update(ref(db, `artists/${id}`), updates);
      setEditingArtist(null);
    } catch (err) {
      console.error("Failed to update artist", err);
      alert("Failed to update artist: " + err.message);
    }
  };

  const deleteArtist = async (id) => {
    setConfirmDialog({
      show: true,
      title: "Delete Artist",
      message: "Delete artist and their metadata? This will NOT delete music files automatically.",
      onConfirm: async () => {
        try {
          await remove(ref(db, `artists/${id}`));
          if (expandedArtist === id) setExpandedArtist(null);
          setConfirmDialog((prev) => ({ ...prev, show: false }));
        } catch (err) {
          console.error("Failed to delete artist", err);
          alert("Failed to delete artist: " + err.message);
        }
      },
      onCancel: () => {
        setConfirmDialog((prev) => ({ ...prev, show: false }));
      }
    });
  };

  const saveTrack = async (trackId, updates) => {
    try {
      await update(ref(db, `artistContent/music/${trackId}`), updates);
      setEditingTrack(null);
    } catch (err) {
      console.error("Failed to update track", err);
      alert("Failed to update track: " + err.message);
    }
  };

  const deleteTrack = async (trackId) => {
    setConfirmDialog({
      show: true,
      title: "Delete Track",
      message: "Delete this track? This removes the DB record only.",
      onConfirm: async () => {
        try {
          await remove(ref(db, `artistContent/music/${trackId}`));
          setEditingTrack(null);
          setConfirmDialog((prev) => ({ ...prev, show: false }));
        } catch (err) {
          console.error("Failed to delete track", err);
          alert("Failed to delete track: " + err.message);
        }
      },
      onCancel: () => {
        setConfirmDialog((prev) => ({ ...prev, show: false }));
      }
    });
  };

  const toggleExpand = (artistId) => {
    setExpandedArtist((prev) => (prev === artistId ? null : artistId));
  };

  const [artistForm, setArtistForm] = useState({});
  useEffect(() => {
    if (editingArtist) setArtistForm({ ...editingArtist });
    else setArtistForm({});
  }, [editingArtist]);

  const [trackForm, setTrackForm] = useState({});
  useEffect(() => {
    if (editingTrack) setTrackForm({ ...editingTrack });
    else setTrackForm({});
  }, [editingTrack]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2a2a33] border-b-gray-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Users size={28} className="text-[#bd9f67]" />
          <div>
            <h1 className="text-2xl font-bold">Artist Management</h1>
            <p className="text-sm text-[#a6a6b5]">
              {artists.length} artists • {music.length} total tracks
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
              placeholder="Search artists, songs, albums..."
              className="pl-10 pr-3 py-2 border border-[#33333d] rounded-lg w-72 bg-transparent text-[#c9c9d4] focus:outline-none focus:ring-2 focus:ring-[#bd9f67]"
            />
            <Search className="absolute left-3 top-2.5 text-[#7d7d8a]" size={18} />
          </div>
        </div>
      </div>

      {/* Artists Grid */}
      {merged.length === 0 ? (
        <div className="text-center py-12 text-[#7d7d8a]">
          <Music size={48} className="mx-auto mb-3 opacity-50" />
          <p>No artists found</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence>
              {paginated.map((artist) => (
                <motion.div
                  key={artist.id}
                  layout
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  className="bg-[#243137] rounded-xl shadow p-4 border-l-4 border-[#bd9f67] hover:border-l-4 hover:border-[#bd9f67] hover:shadow-[0_0_25px_rgba(189,159,103,0.18)] transition"
                >
                  <div className="flex justify-between items-start gap-3">
                    <div>
                      <h2 className="text-lg font-semibold">{artist.name}</h2>
                      <p className="text-sm text-[#a6a6b5]">{artist.email}</p>
                      <div className="mt-2 text-xs inline-block px-3 py-1 rounded-full bg-[#bd9f67]/15 text-[#bd9f67]">
                        {artist.subscriptionTier || "—"}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      <div className="flex gap-2">
                        <button
                          title="Edit artist"
                          onClick={() => setEditingArtist({ id: artist.id, ...artist })}
                          className="p-1 rounded-md hover:bg-[#1e1e25]"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          title="Delete artist"
                          onClick={() => deleteArtist(artist.id)}
                          className="p-1 rounded-md hover:bg-[#1e1e25]"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      <button
                        onClick={() => toggleExpand(artist.id)}
                        className="text-sm text-[#bd9f67] mt-1"
                      >
                        {expandedArtist === artist.id ? "Collapse" : "View music"}
                      </button>
                    </div>
                  </div>

                  {expandedArtist === artist.id && (
                    <div className="mt-4 space-y-3">
                      {Object.keys(artist.albums || {}).length === 0 ? (
                        <p className="text-sm text-[#7d7d8a]">No music uploaded.</p>
                      ) : (
                        Object.entries(artist.albums).map(([albumName, tracks]) => (
                          <div key={albumName} className="bg-[#1e2a2f] p-3 rounded">
                            <div className="flex items-center gap-2 mb-2">
                              <Folder size={16} className="text-[#bd9f67]" />
                              <h3 className="font-medium">{albumName}</h3>
                              <span className="ml-auto text-xs text-[#7d7d8a]">
                                {tracks.length} track{tracks.length !== 1 ? "s" : ""}
                              </span>
                            </div>

                            <ul className="space-y-2">
                              {tracks.map((t) => (
                                <li key={t.id} className="flex items-start gap-3">
                                  <div className="flex-1">
                                    <div className="font-semibold">{t.name}</div>
                                    <div className="text-xs text-[#7d7d8a]">
                                      Track #{t.trackNumber ?? "—"} • {t.type || "song"} •{" "}
                                      <span className="capitalize">{t.status || "—"}</span>
                                    </div>
                                    {t.url && (
                                      <a
                                        href={t.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-xs text-[#bd9f67] underline"
                                      >
                                        Open file
                                      </a>
                                    )}
                                  </div>

                                  <div className="flex flex-col gap-2">
                                    <button
                                      title="Edit track"
                                      onClick={() => setEditingTrack({ ...t })}
                                      className="p-1 rounded-md hover:bg-[#1e1e25]"
                                    >
                                      <Edit2 size={16} />
                                    </button>
                                    <button
                                      title="Delete track"
                                      onClick={() => deleteTrack(t.id)}
                                      className="p-1 rounded-md hover:bg-[#1e1e25]"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-center gap-2 mt-4">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 rounded-md border disabled:opacity-50"
            >
              Prev
            </button>

            {Array.from({ length: totalPages }).map((_, i) => {
              const num = i + 1;
              return (
                <button
                  key={num}
                  onClick={() => setPage(num)}
                  className={`px-3 py-1 rounded-md border ${num === page ? "bg-[#bd9f67] text-[#243137]" : ""}`}
                >
                  {num}
                </button>
              );
            })}

            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 rounded-md border disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Edit Artist Modal */}
      <AnimatePresence>
        {editingArtist && (
          <Modal onClose={() => setEditingArtist(null)}>
            <h3 className="text-lg font-bold mb-3">Edit Artist</h3>
            <label className="text-xs text-[#a6a6b5]">Name</label>
            <input
              value={artistForm.name ?? ""}
              onChange={(e) => setArtistForm((s) => ({ ...s, name: e.target.value }))}
              className="w-full p-2 border border-[#33333d] rounded mb-2 bg-transparent text-[#c9c9d4]"
            />
            <label className="text-xs text-[#a6a6b5]">Email</label>
            <input
              value={artistForm.email ?? ""}
              onChange={(e) => setArtistForm((s) => ({ ...s, email: e.target.value }))}
              className="w-full p-2 border border-[#33333d] rounded mb-2 bg-transparent text-[#c9c9d4]"
            />
            <label className="text-xs text-[#a6a6b5]">Subscription Tier</label>
            <input
              value={artistForm.subscriptionTier ?? ""}
              onChange={(e) => setArtistForm((s) => ({ ...s, subscriptionTier: e.target.value }))}
              className="w-full p-2 border border-[#33333d] rounded mb-4 bg-transparent text-[#c9c9d4]"
            />

            <div className="flex gap-2">
              <button
                onClick={() => saveArtist(editingArtist.id, {
                  name: artistForm.name,
                  email: artistForm.email,
                  subscriptionTier: artistForm.subscriptionTier,
                })}
                className="px-4 py-2 bg-[#bd9f67] hover:bg-[#d0b07a] text-[#243137] rounded"
              >
                Save
              </button>
              <button onClick={() => setEditingArtist(null)} className="px-4 py-2 border rounded">
                Cancel
              </button>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Edit Track Modal */}
      <AnimatePresence>
        {editingTrack && (
          <Modal onClose={() => setEditingTrack(null)}>
            <h3 className="text-lg font-bold mb-3">Edit Track</h3>

            <label className="text-xs text-[#a6a6b5]">Title</label>
            <input
              value={trackForm.name ?? ""}
              onChange={(e) => setTrackForm((s) => ({ ...s, name: e.target.value }))}
              className="w-full p-2 border border-[#33333d] rounded mb-2 bg-transparent text-[#c9c9d4]"
            />
            <label className="text-xs text-[#a6a6b5]">Album</label>
            <input
              value={trackForm.album ?? ""}
              onChange={(e) => setTrackForm((s) => ({ ...s, album: e.target.value }))}
              className="w-full p-2 border border-[#33333d] rounded mb-2 bg-transparent text-[#c9c9d4]"
            />
            <label className="text-xs text-[#a6a6b5]">Track Number</label>
            <input
              type="number"
              value={trackForm.trackNumber ?? ""}
              onChange={(e) => setTrackForm((s) => ({ ...s, trackNumber: Number(e.target.value) }))}
              className="w-full p-2 border border-[#33333d] rounded mb-2 bg-transparent text-[#c9c9d4]"
            />
            <label className="text-xs text-[#a6a6b5]">Status</label>
            <select
              value={trackForm.status ?? "pending"}
              onChange={(e) => setTrackForm((s) => ({ ...s, status: e.target.value }))}
              className="w-full p-2 border border-[#33333d] rounded mb-4 bg-transparent text-[#c9c9d4]"
            >
              <option value="pending">pending</option>
              <option value="approved">approved</option>
              <option value="rejected">rejected</option>
            </select>

            <div className="flex gap-2">
              <button
                onClick={() =>
                  saveTrack(editingTrack.id, {
                    name: trackForm.name,
                    album: trackForm.album,
                    trackNumber: trackForm.trackNumber,
                    status: trackForm.status,
                  })
                }
                className="px-4 py-2 bg-[#bd9f67] hover:bg-[#d0b07a] text-[#243137] rounded"
              >
                Save
              </button>
              <button onClick={() => setEditingTrack(null)} className="px-4 py-2 border rounded">
                Cancel
              </button>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Confirmation Dialog */}
      <AnimatePresence>
        {confirmDialog.show && <ConfirmationDialog data={confirmDialog} />}
      </AnimatePresence>
    </div>
  );
}

function Modal({ children, onClose }) {
  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.96, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#16161b] rounded-xl shadow-2xl max-w-xl w-full p-6"
      >
        <div className="mb-4 flex items-start justify-between">
          <div />
          <button onClick={onClose} className="text-[#7d7d8a] hover:text-white">✕</button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}

function ConfirmationDialog({ data }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#16161b] rounded-xl shadow-xl p-6 max-w-sm w-full"
      >
        <h3 className="text-lg font-bold">{data.title}</h3>
        <p className="text-sm text-[#a6a6b5] mt-2">{data.message}</p>

        <div className="flex justify-end gap-3 mt-5">
          <button
            onClick={() => data.onCancel?.()}
            className="px-4 py-2 border rounded"
          >
            Cancel
          </button>
          <button
            onClick={() => data.onConfirm?.()}
            className="px-4 py-2 bg-red-600 text-white rounded"
          >
            Confirm
          </button>
        </div>
      </motion.div>
    </div>
  );
}